import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, StatCard, Button, Modal, Select, Input } from "@/components/ui";
import { toast } from "sonner";
import { Headphones, PhoneCall, Timer, Gauge, Plus } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const COLORS = ["#312e81", "#4338ca", "#4f46e5", "#6366f1", "#818cf8", "#a5b4fc"];
const REASONS = ["Benefits question", "Appointment scheduling", "Prior auth status", "Provider referral", "Medication refill", "Grievance intake", "Crisis line", "Address update", "Claim inquiry"];

export default function ContactCenter() {
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: "Phone", direction: "Inbound", reason: REASONS[0], disposition: "Resolved", handle_time_sec: 240, notes: "" });

  const load = () => {
    api.get("/interactions").then((r) => setItems(r.data));
    api.get("/interactions-summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    await api.post("/interactions", { ...form, handle_time_sec: parseInt(form.handle_time_sec) || 0, answered: true });
    toast.success("Interaction logged"); setOpen(false); load();
  };

  if (!summary) return <Spinner />;
  const mins = Math.floor(summary.avg_handle_time / 60), secs = summary.avg_handle_time % 60;

  return (
    <div className="vc-fade-up">
      <PageHeader title="Contact Center" subtitle="Omnichannel member & provider service — the single-window customer service the RFP calls for."
        actions={<Button onClick={() => setOpen(true)} data-testid="log-interaction-btn"><Plus className="h-4 w-4" /> Log Interaction</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Interactions" value={summary.total} icon={Headphones} tone="brand" />
        <StatCard label="Calls" value={summary.calls} icon={PhoneCall} tone="blue" />
        <StatCard label="Service Level" value={`${summary.service_level}%`} sub="Target ≥ 80%" icon={Gauge} tone={summary.service_level >= 80 ? "brand" : "red"} />
        <StatCard label="Avg Handle Time" value={`${mins}m ${secs}s`} icon={Timer} tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Interactions by Channel</h3>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart><Pie data={summary.by_channel} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
              {summary.by_channel.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Top Contact Reasons</h3>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={summary.by_reason.slice(0, 7)} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" /><XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} /><Tooltip />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#4f46e5" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {items === null ? <Spinner /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card"><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Member</th><th className="px-4 py-3 font-semibold">Channel</th>
                <th className="px-4 py-3 font-semibold">Direction</th><th className="px-4 py-3 font-semibold">Reason</th>
                <th className="px-4 py-3 font-semibold">Disposition</th><th className="px-4 py-3 font-semibold">Agent</th>
                <th className="px-4 py-3 font-semibold">AHT</th></tr></thead>
              <tbody className="divide-y divide-border">
                {items.map((i) => (
                  <tr key={i.id} className="hover:bg-muted transition-colors">
                    <td className="px-4 py-2.5 text-muted-foreground">{i.member_name || "—"}</td>
                    <td className="px-4 py-2.5"><span className="text-xs rounded bg-slate-100 px-2 py-0.5">{i.channel}</span></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{i.direction}</td>
                    <td className="px-4 py-2.5">{i.reason}</td>
                    <td className="px-4 py-2.5"><Badge tone={i.disposition === "Escalated" ? "High" : "Completed"}>{i.disposition}</Badge></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{i.agent}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{i.handle_time_sec ? `${Math.floor(i.handle_time_sec/60)}m ${i.handle_time_sec%60}s` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Log Interaction">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Channel</label><Select className="w-full mt-1.5" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}><option>Phone</option><option>Email</option><option>Portal Message</option></Select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Direction</label><Select className="w-full mt-1.5" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}><option>Inbound</option><option>Outbound</option></Select></div>
          </div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason</label><Select className="w-full mt-1.5" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>{REASONS.map((r) => <option key={r}>{r}</option>)}</Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Disposition</label><Select className="w-full mt-1.5" value={form.disposition} onChange={(e) => setForm({ ...form, disposition: e.target.value })}><option>Resolved</option><option>Escalated</option><option>Follow-up scheduled</option></Select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Handle Time (sec)</label><Input className="mt-1.5" type="number" value={form.handle_time_sec} onChange={(e) => setForm({ ...form, handle_time_sec: e.target.value })} /></div>
          </div>
          <Button className="w-full" onClick={create} data-testid="interaction-save">Log Interaction</Button>
        </div>
      </Modal>
    </div>
  );
}
